import React, { useState } from 'react';
import { SlackAgentTab } from './admin/SlackAgentTab';
import { UserWorkspaceMappingTab } from './admin/UserWorkspaceMappingTab';
import { UsersListTab } from './admin/UsersListTab';
import { MailboxPoolTab } from './admin/MailboxPoolTab';

type UsersSubTab = 'all-users' | 'slack-agent' | 'workspace-mapping' | 'mailboxes';

const USERS_TABS: { key: UsersSubTab; label: string }[] = [
  { key: 'all-users', label: 'All Users' },
  { key: 'slack-agent', label: 'Slack Agent' },
  { key: 'workspace-mapping', label: 'Workspace Mapping' },
  { key: 'mailboxes', label: 'Mailboxes' },
];

/** "Users" top-level page: system/admin surfaces — All Users, Slack Agent, Workspace
 *  Mapping — kept separate from client-business data (the "Clients" tab). */
export const UsersTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<UsersSubTab>('all-users');
  const [mounted, setMounted] = useState<Record<UsersSubTab, boolean>>({
    'all-users': true,
    'slack-agent': false,
    'workspace-mapping': false,
    mailboxes: false,
  });

  const switchTab = (key: UsersSubTab) => {
    setMounted((m) => (m[key] ? m : { ...m, [key]: true }));
    setActiveTab(key);
  };

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div
          className="inline-flex items-center gap-1 bg-neutral-100 rounded-full p-1"
          role="tablist"
          aria-label="Users section"
        >
          {USERS_TABS.map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => switchTab(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeTab === key
                  ? 'bg-white text-primary-700 shadow-card'
                  : 'text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className={activeTab === 'all-users' ? '' : 'hidden'}>
        {mounted['all-users'] && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <UsersListTab />
          </div>
        )}
      </div>
      <div className={activeTab === 'slack-agent' ? '' : 'hidden'}>
        {mounted['slack-agent'] && <SlackAgentTab />}
      </div>
      <div className={activeTab === 'workspace-mapping' ? '' : 'hidden'}>
        {mounted['workspace-mapping'] && <UserWorkspaceMappingTab />}
      </div>
      <div className={activeTab === 'mailboxes' ? '' : 'hidden'}>
        {mounted.mailboxes && <MailboxPoolTab />}
      </div>
    </div>
  );
};

export default UsersTabs;
